<script>
import useSharedData from "@/use/SharedData.js";
import useValidation from "@/use/Validation.js";
import useTools from "@/use/Tools.js";
import {computed, onMounted, ref} from "vue";
import {useRecruitmentRequestStore} from "@/stores/Request/RecruitmentRequest.js";
import useSharedMethod from "@/use/SharedMethod.js";
import WCodeGeneration from "@/components/WCodeGeneration.vue";
import axios from "axios";
import useSharedSelectData from "@/use/SharedSelectData";

export default {
    name: "RecruitmentRequestForm",
    components: {WCodeGeneration},
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        deleteAction: Boolean,
        viewAction: Boolean,
    },

    setup(props) {
        const {mxRules} = useValidation();
        const {loading, dialog, overlay} = useSharedData();
        const {recruitmentScopes} = useSharedSelectData();
        const {$route, $refs} = useTools();
        const {getBack} = useSharedMethod();

        const recruitmentRequestStore = useRecruitmentRequestStore();

        const readonly = computed(() => {
            return props.viewAction || props.deleteAction;
        });

        const recruitmentRequest = ref({
            checkFirstNameCompleted: true,
            checkLastNameCompleted: true,
            checkWilayaCompleted: true,
        });


        function findById(id) {
            loading.value = true;
            axios.get(`/recruitmentRequests/${id}`).then((response) => {
                recruitmentRequest.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }

        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.post('/recruitmentRequests', recruitmentRequest.value).then((response) => {
                    recruitmentRequestStore.addRecruitmentRequest(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.put('/recruitmentRequests', recruitmentRequest.value).then((response) => {
                    recruitmentRequestStore.updateRecruitmentRequest(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function remove() {
            loading.value = true;
            axios.delete(`/recruitmentRequests/${recruitmentRequest.value.id}`).then(() => {
                recruitmentRequestStore.deleteRecruitmentRequest(recruitmentRequest.value);
                getBack();
            }).finally(() => {
                loading.value = false;
            });
        }

        onMounted(() => {
            if (!props.addAction) {
                findById($route.params.id);
            }
        });

        return {
            mxRules,
            recruitmentRequest,
            readonly,
            loading,
            dialog,
            overlay,
            recruitmentScopes,
            save,
            update,
            remove,
            getBack,
        };
    },
};
</script>

<template>
    <v-dialog
        v-model="dialog"
        persistent
        max-width="1100"
        scrollable
    >
        <v-card>
            <w-dialog-toolbar
                :add-action="addAction"
                :update-action="updateAction"
                :delete-action="deleteAction"
                :view-action="viewAction"
            />
            <w-overlay
                :loading="loading"
                :overlay="overlay"
                :delete-action="deleteAction"
                @remove="remove"
                @cancel="overlay = false"
            />

            <v-card-text>
                <v-form ref="form" lazy-validation>
                    <v-container fluid>
                        <w-fieldset :title="$t('common.details')">
                            <v-row>
                                <v-col cols="12" md="4">
                                    <w-label :label="$t('common.code')" required/>
                                    <w-code-generation
                                        v-model="recruitmentRequest.code"
                                        :entity="'RECRUITMENT'"
                                        :load="addAction"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12" md="4">
                                    <w-label :label="$t('recruitment_request.requesting_agency')" required/>
                                    <organizational-chart-field
                                        v-model="recruitmentRequest.requestingAgencyId"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12" md="4">
                                    <w-label :label="$t('recruitment_request.applicant')" required/>
                                    <str-worker-autocomplete
                                        v-model="recruitmentRequest.workerId"
                                        action="SAVE_RECRUITMENT_REQUEST"
                                        :readonly="readonly"
                                        :organizationalchart-id="recruitmentRequest.requestingAgencyId"
                                        load-by-organizationalchart-id
                                        :disabled="!recruitmentRequest.requestingAgencyId"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>

                                <v-col cols="12" md="4">
                                    <w-label :label="$t('recruitment_request.occupation')" required/>
                                    <str-worker-def-autocomplete
                                        v-model="recruitmentRequest.occupationId"
                                        :resource="'occupations'"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12" md="4">
                                    <w-label :label="$t('recruitment_request.post_number')" required/>
                                    <w-text-field
                                        v-model="recruitmentRequest.postNumber"
                                        :readonly="readonly"
                                        type="number"
                                        :rules="[mxRules.required, mxRules.integer]"
                                    />
                                </v-col>
                                <v-col cols="12" md="4">
                                    <w-label :label="$t('recruitment_request.reason')" required/>
                                    <w-data-select-autocomplete
                                        v-model="recruitmentRequest.reasonId"
                                        type="RECRUITMENT_REASON"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>

                                <v-col cols="12" md="4">
                                    <w-label :label="$t('recruitment_request.type')" required/>
                                    <w-enum-select
                                        v-model="recruitmentRequest.scope"
                                        :items="recruitmentScopes"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12" md="4">
                                    <w-label :label="$t('recruitment_request.budget')" required/>
                                    <w-data-select-autocomplete
                                        v-model="recruitmentRequest.budgetId"
                                        type="RECRUITMENT_BUDGET"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12" md="4">
                                    <w-label :label="$t('recruitment_request.recruitment_date')" required/>
                                    <w-date-picker
                                        v-model="recruitmentRequest.expectedDate"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>

                                <v-col cols="12">
                                    <w-label :label="$t('common.description')"/>
                                    <w-simple-text-area
                                        v-model="recruitmentRequest.description"
                                        :readonly="readonly"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                    </v-container>
                </v-form>
            </v-card-text>
            <v-divider/>
            <v-card-actions dense>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-delete
                    :show="deleteAction"
                    @press="overlay = true"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>